import React from 'react';
import { XCircleIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const SchedulingIssuesDrawer = ({
  open,
  onClose,
  schedulingFeasibility,
  isSessionExcluded,
  handleExcludeSession,
  handleIncludeSession,
  handleExcludeCrewAllSessions,
  handleIncludeCrewAllSessions,
  openAdjustAvailabilityModal,
  isUpdatingCrew,
  formatDate,
  sessionOverrides = {},
  handleClearSessionOverride,
  getUserInfo,
}) => {
  if (!open) return null;

  // Helper function to check if a session has overrides
  const hasSessionOverride = (sessionId) => {
    // Check for any crew-specific overrides for this session
    const hasAnyCrewOverride = Object.keys(sessionOverrides).some((key) => {
      if (key.startsWith(`${sessionId}-`)) {
        const entry = sessionOverrides[key];
        const mo = entry?.member_overrides;
        return mo && typeof mo === 'object' && Object.keys(mo).length > 0;
      }
      return false;
    });
    return hasAnyCrewOverride;
  };

  // Helper function to check if a session has overrides for a specific crew
  const hasSessionOverrideForCrew = (sessionId, crewId) => {
    const compositeKey = `${sessionId}-${crewId}`;
    const stringKey = String(compositeKey);
    const override = sessionOverrides[compositeKey] || sessionOverrides[stringKey];

    // Handle nested structure: override might be { "139-172": { member_overrides: {...} } }
    if (override && typeof override === 'object') {
      // Check if it's a nested structure
      const nestedOverride = override[compositeKey] || override[stringKey];
      if (
        nestedOverride &&
        nestedOverride.member_overrides &&
        Object.keys(nestedOverride.member_overrides).length > 0
      ) {
        return true;
      }
      // Check if it's a direct structure
      if (override.member_overrides && Object.keys(override.member_overrides).length > 0) {
        return true;
      }
    }

    return false;
  };

  // Helper function to get override details for display
  const getOverrideDetails = (sessionId, crewId) => {
    const compositeKey = `${sessionId}-${crewId}`;
    const stringKey = String(compositeKey);
    const override = sessionOverrides[compositeKey] || sessionOverrides[stringKey];

    // Handle nested structure: override might be { "139-172": { member_overrides: {...} } }
    let actualOverride = null;
    if (override && typeof override === 'object') {
      // Check if it's a nested structure
      const nestedOverride = override[compositeKey] || override[stringKey];
      if (
        nestedOverride &&
        nestedOverride.member_overrides &&
        Object.keys(nestedOverride.member_overrides).length > 0
      ) {
        actualOverride = nestedOverride;
      } else if (override.member_overrides && Object.keys(override.member_overrides).length > 0) {
        // Check if it's a direct structure
        actualOverride = override;
      }
    }

    if (!actualOverride || !actualOverride.member_overrides) return null;

    const memberCount = Object.keys(actualOverride.member_overrides).length;
    const members = Object.keys(actualOverride.member_overrides).map((uid) => {
      const info = getUserInfo ? getUserInfo(parseInt(uid)) : null;
      return info ? info.name : `User ${uid}`;
    });

    // Enhanced override information
    const crewInfo = {
      crewId: actualOverride.crew_id,
      crewName: actualOverride.crew_name || `Crew ${actualOverride.crew_id}`,
      boatType: actualOverride.boat_type || 'Unknown',
    };

    const timeInfo = {
      windowStart: actualOverride.window_start,
      windowEnd: actualOverride.window_end,
      duration: actualOverride.duration_minutes,
    };

    const metadata = {
      createdAt: actualOverride.created_at,
      originalIssue: actualOverride.original_issue,
      totalMembersAffected: actualOverride.total_members_affected || memberCount,
    };

    return {
      memberCount,
      members,
      crewInfo,
      timeInfo,
      metadata,
    };
  };

  const crews = schedulingFeasibility?.crew_analysis || [];
  const crewsWithIssues = crews.filter((crew) => {
    const hasNonFeasibleSessions = (crew.session_feasibility || []).some((sf) => !sf.feasible);
    // Consider overrides regardless of feasibility so they remain visible immediately after save
    const hasOverrides = (crew.session_feasibility || []).some((sf) =>
      hasSessionOverrideForCrew(sf.session_id, crew.crew_id)
    );
    const hasExcludedSessions = (crew.session_feasibility || []).some((sf) => sf.excluded);

    // Show crew if it has any issues, overrides, or excluded sessions
    return hasNonFeasibleSessions || hasOverrides || hasExcludedSessions;
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative flex max-h-[90vh] w-full max-w-4xl flex-col bg-white shadow-xl">
        <div className="flex flex-shrink-0 items-center justify-between border-b px-4 py-3">
          <h3 className="text-base font-medium text-gray-900">Scheduling Issues</h3>
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">
            Close
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {crewsWithIssues.length === 0 ? (
            <div className="py-8 text-center">
              <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No Scheduling Issues</h3>
              <p className="mt-1 text-sm text-gray-500">All crews can be scheduled successfully.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Review and resolve issues preventing crews from being scheduled. You can exclude
                non-feasible sessions or adjust availability for some members.
              </p>

              {crewsWithIssues.map((crew) => {
                const nonFeasible = (crew.session_feasibility || []).filter(
                  (sf) => !sf.feasible && !sf.excluded
                );
                // Include overrides regardless of feasibility so they don't disappear
                const sessionsWithOverrides = (crew.session_feasibility || []).filter((sf) =>
                  hasSessionOverrideForCrew(sf.session_id, crew.crew_id)
                );
                const excludedSessions = (crew.session_feasibility || []).filter(
                  (sf) => sf.excluded
                );
                // Deduplicate by session_id, preferring non-feasible entry when present
                const bySession = new Map();
                [...sessionsWithOverrides, ...excludedSessions, ...nonFeasible].forEach((sf) => {
                  const key = sf.session_id;
                  if (!bySession.has(key)) bySession.set(key, sf);
                  // If already present but this version is non-feasible, prefer it
                  if (bySession.has(key) && !sf.feasible && !sf.excluded) {
                    bySession.set(key, sf);
                  }
                });
                const allSessionsToShow = Array.from(bySession.values());
                const crewSessionIds = (crew.session_feasibility || [])
                  .map((s) => s.session_id)
                  .filter(Boolean);
                const loading = Boolean(isUpdatingCrew[crew.crew_id]);
                const isFullyExcluded =
                  crewSessionIds.length > 0 &&
                  crewSessionIds.every((sid) => isSessionExcluded(crew.crew_id, sid));

                return (
                  <div key={crew.crew_id} className="rounded-lg border border-gray-200 p-4">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <h4 className="text-sm font-medium text-gray-900">
                          {crew.crew_name} ({crew.boat_type})
                        </h4>
                        <div className="text-xs text-gray-600">
                          {nonFeasible.length} non-feasible session
                          {nonFeasible.length === 1 ? '' : 's'}
                          {sessionsWithOverrides.length > 0 && (
                            <>
                              {' '}
                              • {sessionsWithOverrides.length} session
                              {sessionsWithOverrides.length === 1 ? '' : 's'} with override
                              {sessionsWithOverrides.length === 1 ? '' : 's'}
                            </>
                          )}
                          {excludedSessions.length > 0 && (
                            <>
                              {' '}
                              • {excludedSessions.length} excluded session
                              {excludedSessions.length === 1 ? '' : 's'}
                            </>
                          )}
                          • {crew.total_members} members
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {loading && (
                          <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] text-gray-600">
                            Updating…
                          </span>
                        )}
                        {!loading &&
                          (isFullyExcluded ? (
                            <button
                              onClick={() =>
                                handleIncludeCrewAllSessions(crew.crew_id, crewSessionIds)
                              }
                              className="rounded bg-gray-600 px-2 py-1 text-xs text-white hover:bg-gray-700"
                              title="Include this crew for all sessions"
                            >
                              Include Crew
                            </button>
                          ) : (
                            <button
                              onClick={() =>
                                handleExcludeCrewAllSessions(crew.crew_id, crewSessionIds)
                              }
                              className="rounded bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700"
                              title="Exclude this crew for all sessions"
                            >
                              Exclude Crew
                            </button>
                          ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      {allSessionsToShow.map((sf, idx) => {
                        const excluded = isSessionExcluded(crew.crew_id, sf.session_id);
                        const hasOverride = hasSessionOverrideForCrew(sf.session_id, crew.crew_id);
                        const overrideDetails = hasOverride
                          ? getOverrideDetails(sf.session_id, crew.crew_id)
                          : null;

                        return (
                          <div
                            key={`${crew.crew_id}-${sf.session_id}-${idx}`}
                            className="flex items-center justify-between rounded border p-3 text-sm"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-gray-900">
                                {formatDate(sf.session_date)}
                              </div>
                              <div
                                className={`mt-0.5 text-xs ${excluded ? 'text-gray-600' : sf.feasible ? 'text-green-600' : 'text-red-600'}`}
                              >
                                {excluded
                                  ? 'Session excluded by user'
                                  : sf.feasible
                                    ? 'Feasible with override'
                                    : sf.reason}
                              </div>
                              {hasOverride && overrideDetails && (
                                <div className="mt-1 rounded border border-blue-200 bg-blue-50 p-2">
                                  <div className="mb-1 flex items-center gap-1">
                                    <span className="text-xs font-medium text-blue-800">
                                      Override applied
                                    </span>
                                    <span className="text-xs text-blue-600">
                                      ({overrideDetails.memberCount} member
                                      {overrideDetails.memberCount === 1 ? '' : 's'})
                                    </span>
                                  </div>
                                  <div className="mb-1 text-xs text-blue-700">
                                    {overrideDetails.members.slice(0, 2).join(', ')}
                                    {overrideDetails.members.length > 2 &&
                                      ` +${overrideDetails.members.length - 2} more`}
                                  </div>
                                  {overrideDetails.timeInfo &&
                                    overrideDetails.timeInfo.windowStart && (
                                      <div className="mb-1 text-xs text-blue-600">
                                        Window: {overrideDetails.timeInfo.windowStart} -{' '}
                                        {overrideDetails.timeInfo.windowEnd} (
                                        {overrideDetails.timeInfo.duration}min)
                                      </div>
                                    )}
                                  {overrideDetails.crewInfo &&
                                    overrideDetails.crewInfo.crewName && (
                                      <div className="text-xs text-blue-600">
                                        Crew: {overrideDetails.crewInfo.crewName} (
                                        {overrideDetails.crewInfo.boatType})
                                      </div>
                                    )}
                                </div>
                              )}
                            </div>
                            <div className="ml-3 flex gap-1.5">
                              {!excluded ? (
                                <>
                                  {hasOverride ? (
                                    // When an override exists, only allow clearing it
                                    <button
                                      onClick={() =>
                                        handleClearSessionOverride(sf.session_id, crew.crew_id)
                                      }
                                      className="rounded border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
                                      title="Clear override"
                                    >
                                      Clear
                                    </button>
                                  ) : (
                                    <>
                                      <button
                                        onClick={() => openAdjustAvailabilityModal(crew.crew_id, sf)}
                                        className="rounded bg-blue-600 px-2.5 py-1 text-xs text-white hover:bg-blue-700"
                                        title="Adjust availability for this session"
                                      >
                                        Adjust
                                      </button>
                                      <button
                                        onClick={() =>
                                          handleExcludeSession(crew.crew_id, sf.session_id)
                                        }
                                        className="rounded border border-red-300 bg-white px-2.5 py-1 text-xs text-red-600 hover:bg-red-50"
                                        title="Exclude this session from scheduling"
                                      >
                                        Exclude
                                      </button>
                                    </>
                                  )}
                                </>
                              ) : (
                                <>
                                  <span className="rounded bg-gray-100 px-2 py-1 text-[10px] text-gray-600">
                                    Excluded
                                  </span>
                                  <button
                                    onClick={() =>
                                      handleIncludeSession(crew.crew_id, sf.session_id)
                                    }
                                    className="rounded border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
                                    title="Include this session"
                                  >
                                    Include
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchedulingIssuesDrawer;
